import { useState } from "react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import CircularLoader from "../common/CircularLoader/CircularLoader";
import inactiveCheckIcon from "../../assets/images/bankStatements/inactive-check.svg";
import undoIcon from "../../assets/images/bankStatements/undo.svg";
import {
  PulseBankAccount,
  PulseRangeErrorType,
  PulseRangeUploadInput,
} from "./types";
import {
  formatMissingRange,
  getTransactionConsentKey,
  getVisibleRangeErrors,
  pluralize,
} from "./utils";
import { useBankStatementsActions } from "./BankStatementsContext";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import styles from "./BankStatements.module.scss";

const EMPTY_CONSENT_MAP: Record<string, { consented: boolean }> = {};

const compareRangesByDate = (left: string[], right: string[]) =>
  String(left[0] || "").localeCompare(String(right[0] || "")) ||
  String(left[1] || "").localeCompare(String(right[1] || ""));

export const MissingPeriodPanel = ({
  account,
  onUpload,
  isRangeProcessing,
}: {
  account: PulseBankAccount;
  onUpload: (rangeUpload: PulseRangeUploadInput) => void;
  isRangeProcessing: (rangeUpload: PulseRangeUploadInput) => boolean;
}) => {
  const errorDetails = account.lastAnalysis?.errorDetails;
  const { setNoTransactionConsent } = useBankStatementsActions();
  const {
    consentableErrorType,
    transactionRanges,
    truncatedRangeKeys,
    balanceRanges,
  } = getVisibleRangeErrors(errorDetails);
  const hasBalanceReconciliationFailure = balanceRanges.length > 0;
  const consentMap =
    errorDetails?.noTransactionConsent || EMPTY_CONSENT_MAP;
  const [consentRange, setConsentRange] = useState<string[] | null>(null);
  const [pendingConsent, setPendingConsent] = useState<{
    key: string;
    action: "confirm" | "undo";
  } | null>(null);
  const [consentError, setConsentError] = useState("");
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const sortedTransactionRanges = [...transactionRanges].sort(
    compareRangesByDate
  );
  const unresolvedTransactionRanges = sortedTransactionRanges.filter(
    (range) => !consentMap[getTransactionConsentKey(range)]?.consented
  );
  const inactiveTransactionRanges = sortedTransactionRanges.filter(
    (range) => !!consentMap[getTransactionConsentKey(range)]?.consented
  );
  const orderedTransactionRanges = [
    ...unresolvedTransactionRanges,
    ...inactiveTransactionRanges,
  ];
  const allTransactionRangesInactive =
    sortedTransactionRanges.length > 0 &&
    unresolvedTransactionRanges.length === 0;
  const hasConsentableUnresolvedRange = unresolvedTransactionRanges.some(
    (range) => !truncatedRangeKeys.has(getTransactionConsentKey(range))
  );
  const hasTruncatedUnresolvedRange = unresolvedTransactionRanges.some(
    (range) => truncatedRangeKeys.has(getTransactionConsentKey(range))
  );

  if (!hasBalanceReconciliationFailure && !transactionRanges.length) {
    return null;
  }

  const submitNoActivityConsent = async () => {
    if (!consentRange) return;
    const consentKey = getTransactionConsentKey(consentRange);
    setPendingConsent({ key: consentKey, action: "confirm" });
    setConsentError("");
    try {
      await setNoTransactionConsent(account.id, consentRange, true);
      setRowErrors((current) => {
        const next = { ...current };
        delete next[consentKey];
        return next;
      });
      setConsentRange(null);
    } catch (error) {
      setConsentError(
        getApiErrorMessage(error, "Your confirmation could not be saved.")
      );
    } finally {
      setPendingConsent(null);
    }
  };

  const undoNoActivityConsent = async (range: string[]) => {
    const consentKey = getTransactionConsentKey(range);
    setPendingConsent({ key: consentKey, action: "undo" });
    setRowErrors((current) => {
      const next = { ...current };
      delete next[consentKey];
      return next;
    });
    try {
      await setNoTransactionConsent(account.id, range, false);
    } catch (error) {
      setRowErrors((current) => ({
        ...current,
        [consentKey]:
          getApiErrorMessage(error, "Your confirmation could not be undone."),
      }));
    } finally {
      setPendingConsent(null);
    }
  };

  const renderRanges = (
    ranges: string[][],
    allowConsent: boolean,
    errorType: PulseRangeErrorType
  ) => (
    <div className={styles.missingRangeList}>
      {ranges.map((range) => {
        const consentKey = getTransactionConsentKey(range);
        const canConsent =
          allowConsent && !truncatedRangeKeys.has(consentKey);
        const rangeUpload: PulseRangeUploadInput = {
          errorType,
          rangeStart: range[0],
          rangeEnd: range[1],
        };
        const rangeProcessing = isRangeProcessing(rangeUpload);
        const confirmed = !!consentMap[consentKey]?.consented;
        const isConfirmationOpen =
          canConsent &&
          !confirmed &&
          !!consentRange &&
          getTransactionConsentKey(consentRange) === consentKey;
        const isConfirming =
          pendingConsent?.key === consentKey &&
          pendingConsent.action === "confirm";
        const isUndoing =
          pendingConsent?.key === consentKey &&
          pendingConsent.action === "undo";
        return (
          <div
            className={`${styles.missingRangeRow} ${
              isConfirmationOpen ? styles.expandedMissingRangeRow : ""
            }`}
            key={consentKey}
          >
            <div className={styles.missingRangeSummary}>
              <span className={styles.missingRangeLabel}>
                {formatMissingRange(range)}
              </span>
              {rangeProcessing ? (
                <span className={styles.rangeProcessingStatus} role="status">
                  <span
                    className={styles.rangeProcessingLoader}
                    aria-hidden="true"
                  >
                    <CircularLoader width="0.875rem" height="0.875rem" />
                  </span>
                  Checking statement
                </span>
              ) : !isConfirmationOpen ? (
                <div
                  className={`${styles.missingRangeActions} ${
                    confirmed ? styles.inactiveRangeActions : ""
                  }`}
                >
                  {canConsent && confirmed ? (
                    <>
                      <span className={styles.inactiveRangeStatus}>
                        <img src={inactiveCheckIcon} alt="" />
                        Marked inactive
                      </span>
                      <MeritoButton
                        type="tertiary"
                        size="md2"
                        icon={
                          <img
                            className={styles.undoIcon}
                            src={undoIcon}
                            alt=""
                          />
                        }
                        hover={false}
                        disabled={!!pendingConsent}
                        loading={isUndoing}
                        btnText="Undo"
                        onClick={() => undoNoActivityConsent(range)}
                      />
                    </>
                  ) : canConsent ? (
                    <MeritoButton
                      type="tertiary"
                      size="md"
                      className={styles.missingRangeActionButton}
                      disabled={!!pendingConsent}
                      loading={isConfirming}
                      btnText="Confirm No Activity"
                      onClick={() => {
                        setConsentError("");
                        setConsentRange(range);
                      }}
                    />
                  ) : null}
                  {!canConsent || !confirmed ? (
                    <MeritoButton
                      type="inverse"
                      size="md"
                      className={styles.missingRangeActionButton}
                      disabled={!!pendingConsent}
                      btnText="Upload Statement"
                      onClick={() => onUpload(rangeUpload)}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
            {!rangeProcessing && isConfirmationOpen ? (
              <div
                className={styles.noActivityConfirmation}
                aria-label={`Confirm no activity for ${formatMissingRange(range)}`}
              >
                <p>
                  Confirm there were no transactions from {formatMissingRange(range)}.
                </p>
                {consentError ? (
                  <div className={styles.missingRangeError}>{consentError}</div>
                ) : null}
                <div className={styles.noActivityConfirmationActions}>
                  <MeritoButton
                    type="primary-1"
                    size="md"
                    btnText="Confirm"
                    disabled={!!pendingConsent}
                    loading={isConfirming}
                    onClick={submitNoActivityConsent}
                  />
                  <MeritoButton
                    type="tertiary"
                    size="md"
                    btnText="Cancel"
                    disabled={!!pendingConsent}
                    onClick={() => {
                      setConsentError("");
                      setConsentRange(null);
                    }}
                  />
                </div>
              </div>
            ) : !rangeProcessing && rowErrors[consentKey] ? (
              <div className={styles.missingRangeError}>
                {rowErrors[consentKey]}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {hasBalanceReconciliationFailure ? (
        <div className={styles.missingPeriodPanel}>
          <div className={styles.missingPeriodHeader}>
            <strong><HiOutlineExclamationCircle /> Closing and opening balances don’t match.</strong>
            <span>Upload a statement for each exact period shown.</span>
          </div>
          {renderRanges(balanceRanges, false, "balance_recon_ranges")}
        </div>
      ) : null}
      {transactionRanges.length ? (
        <div
          className={`${styles.missingPeriodPanel} ${
            allTransactionRangesInactive
              ? styles.allInactivePeriodPanel
              : ""
          }`}
        >
          <div className={styles.missingPeriodHeader}>
            <strong>
              <HiOutlineExclamationCircle />
              {allTransactionRangesInactive
                ? `${pluralize(inactiveTransactionRanges.length, "period")} confirmed with no transactions.`
                : `Transactions are missing for ${pluralize(unresolvedTransactionRanges.length, "period")}.`}
            </strong>
            <span>
              {allTransactionRangesInactive
                ? "You can undo any of these if you need to upload a statement instead."
                : hasConsentableUnresolvedRange && hasTruncatedUnresolvedRange
                ? "Upload statements for these periods, or confirm no activity if applicable."
                : hasConsentableUnresolvedRange
                ? "Upload a statement, or confirm the account had no activity during this time."
                : "The uploaded statement looks incomplete. Upload the complete statement for each period shown."}
            </span>
          </div>
          {renderRanges(orderedTransactionRanges, true, consentableErrorType)}
        </div>
      ) : null}
    </>
  );
};
